/**
 * bw-home.js
 * Shared homepage slider engine.
 * Reachable-position pagination; preserves collection loop-height correction.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function BwSlider(root) {
    this.root = root;
    this.track = root.querySelector('[data-bw-slider-track]');
    if (!this.track) return;

    this.slides = Array.prototype.slice.call(
      this.track.querySelectorAll('[data-bw-slide]')
    );
    if (!this.slides.length) return;

    this.prev = root.querySelector('[data-bw-slider-prev]');
    this.next = root.querySelector('[data-bw-slider-next]');
    this.dotsHost = root.querySelector('[data-bw-slider-dots]');

    this.centerActive =
      root.getAttribute('data-bw-center-active') === 'true';

    this.loop =
      root.getAttribute('data-bw-loop') === 'true';

    this.interval =
      parseInt(root.getAttribute('data-bw-autoplay'), 10) || 0;

    this.realCount = this.slides.length;
    this.offset = 0;
    this.index = 0;
    this.timer = null;

    if (this.loop && this.realCount > 1) {
      this.buildLoop();
    }

    this.refreshPositions();
    this.buildDots();
    this.bind();
    this.sync();

    if (this.interval > 0 && !reduceMotion) {
      this.start();
    }
  }

  BwSlider.prototype.buildLoop = function () {
    var track = this.track;
    var originals = this.slides;
    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();

    // Clone in forward order on both sides.
    for (var i = 0; i < originals.length; i++) {
      var head = originals[i].cloneNode(true);
      var tail = originals[i].cloneNode(true);

      [head, tail].forEach(function (clone) {
        clone.setAttribute('data-bw-clone', '');
        clone.setAttribute('aria-hidden', 'true');

        if (clone.tagName === 'A') {
          clone.setAttribute('tabindex', '-1');
        }

        var links = clone.querySelectorAll(
          'a, button, input, select, textarea'
        );

        for (var k = 0; k < links.length; k++) {
          links[k].setAttribute('tabindex', '-1');
        }
      });

      after.appendChild(tail);
      before.appendChild(head);
    }

    track.insertBefore(before, track.firstChild);
    track.appendChild(after);

    this.slides = Array.prototype.slice.call(
      track.querySelectorAll('[data-bw-slide]')
    );

    this.offset = this.realCount;

    var self = this;

    // Initialize the expanding collection without animating
    // between cloned and original cards.
    if (this.centerActive) {
      this.index = this.offset;
      this.instantCenterJump(this.offset * this.step());
    } else {
      requestAnimationFrame(function () {
        self.index = self.offset;
        self.jumpTo(self.offset * self.step());
        self.paint();
      });
    }
  };

  BwSlider.prototype.step = function () {
    var first = this.slides[0];
    var second = this.slides[1];

    if (!second) {
      return first.getBoundingClientRect().width || 1;
    }

    var gap = Math.round(
      second.getBoundingClientRect().left -
      first.getBoundingClientRect().left
    );

    return gap > 0
      ? gap
      : (first.getBoundingClientRect().width || 1);
  };

  BwSlider.prototype.jumpTo = function (left) {
    var track = this.track;
    var previous = track.style.scrollBehavior;

    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;

    void track.offsetWidth;

    track.style.scrollBehavior = previous || '';
  };

  /*
   * Infinite-loop active-card synchronization.
   *
   * Temporarily disable height transitions, transfer the
   * active state, jump, then restore transitions.
   *
   * Only applies to center-active sliders.
   */
  BwSlider.prototype.instantCenterJump = function (left) {
    if (!this.centerActive) {
      this.jumpTo(left);
      return;
    }

    var self = this;
    var track = this.track;
    var slides = this.slides;

    if (this.centerJumpFrame) {
      window.cancelAnimationFrame(this.centerJumpFrame);
    }

    if (!this.centerJumpTransitions) {
      this.centerJumpTransitions = slides.map(function (slide) {
        return slide.style.transition;
      });
    }

    slides.forEach(function (slide) {
      slide.style.transition = 'none';
    });

    var previousSnap = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';

    this.jumpTo(left);
    this.markCenter();

    track.style.scrollSnapType = previousSnap;

    // Commit the new active height before restoring animation.
    void track.offsetWidth;

    this.centerJumpFrame = requestAnimationFrame(function () {
      slides.forEach(function (slide, i) {
        slide.style.transition =
          self.centerJumpTransitions[i];
      });

      self.centerJumpTransitions = null;
      self.centerJumpFrame = null;
    });
  };

  BwSlider.prototype.normalize = function () {
    if (!this.loop) return;

    var setWidth = this.realCount * this.step();
    if (setWidth <= 0) return;

    var left = this.track.scrollLeft;

    if (left < setWidth * 0.5) {
      this.instantCenterJump(left + setWidth);
    } else if (left >= setWidth * 1.5) {
      this.instantCenterJump(left - setWidth);
    }
  };

  /*
   * Non-looping sliders use one dot per distinct reachable
   * scroll position. Targets beyond the end are consolidated
   * into a single final position.
   */
  BwSlider.prototype.refreshPositions = function () {
    if (this.loop) return;

    var track = this.track;
    var max = Math.max(
      0,
      track.scrollWidth - track.clientWidth
    );

    var origin =
      this.slides[0].getBoundingClientRect().left;

    var positions = [0];

    for (var i = 1; i < this.slides.length; i++) {
      var left =
        this.slides[i].getBoundingClientRect().left - origin;

      var target = Math.max(
        0,
        Math.min(left, max)
      );

      if (target - positions[positions.length - 1] > 1) {
        positions.push(target);
      }
    }

    if (max - positions[positions.length - 1] > 1) {
      positions.push(max);
    }

    this.positions = positions;
  };

  BwSlider.prototype.refresh = function () {
    this.refreshPositions();

    var count = this.loop
      ? this.realCount
      : this.positions.length;

    if (!this.dots || this.dots.length !== count) {
      this.buildDots();
    }

    this.sync();
  };

  BwSlider.prototype.buildDots = function () {
    if (!this.dotsHost) return;

    this.dotsHost.setAttribute('role', 'group');
    this.dotsHost.innerHTML = '';
    this.dots = [];

    var count = this.loop
      ? this.realCount
      : this.positions.length;

    for (var i = 0; i < count; i++) {
      var dot = document.createElement('button');

      dot.type = 'button';
      dot.className = 'bw-slider__dot';

      dot.setAttribute(
        'aria-label',
        'Go to slider position ' + (i + 1) + ' of ' + count
      );

      dot.dataset.index = String(i);

      this.dotsHost.appendChild(dot);
      this.dots.push(dot);
    }
  };

  BwSlider.prototype.bind = function () {
    var self = this;

    if (this.prev) {
      this.prev.addEventListener('click', function () {
        self.go(self.index - 1, true);
      });
    }

    if (this.next) {
      this.next.addEventListener('click', function () {
        self.go(self.index + 1, true);
      });
    }

    if (this.dotsHost) {
      this.dotsHost.addEventListener('click', function (event) {
        var dot = event.target.closest('.bw-slider__dot');
        if (!dot) return;

        var i = parseInt(dot.dataset.index, 10) || 0;

        self.go(self.offset + i, true);
      });
    }

    var scrolling;

    this.track.addEventListener('scroll', function () {
      window.clearTimeout(scrolling);

      scrolling = window.setTimeout(function () {
        self.sync();
      }, 90);

      if (self.centerActive) {
        self.markCenter();
      }
    }, { passive: true });

    ['mouseenter', 'focusin', 'touchstart'].forEach(function (evt) {
      self.root.addEventListener(evt, function () {
        self.stop();
      }, { passive: true });
    });

    ['mouseleave', 'focusout'].forEach(function (evt) {
      self.root.addEventListener(evt, function () {
        if (self.interval > 0 && !reduceMotion) {
          self.start();
        }
      });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.stop();
      } else if (self.interval > 0 && !reduceMotion) {
        self.start();
      }
    });

    window.addEventListener('resize', function () {
      self.refresh();
    });

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(function () {
        self.refresh();
      });

      this.resizeObserver.observe(this.track);

      this.slides.forEach(function (slide) {
        self.resizeObserver.observe(slide);
      });
    }

    this.track.addEventListener('load', function () {
      self.refresh();
    }, true);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        self.refresh();
      });
    }
  };

  BwSlider.prototype.go = function (index, smooth) {
    if (!this.loop) {
      var last = this.positions.length - 1;

      if (index < 0) index = last;
      if (index > last) index = 0;
    }

    this.index = index;

    this.track.scrollTo({
      left: this.loop
        ? index * this.step()
        : this.positions[index],

      behavior: (smooth && !reduceMotion)
        ? 'smooth'
        : 'auto'
    });

    this.paint();
  };

  BwSlider.prototype.sync = function () {
    var step = this.step();

    this.normalize();

    if (this.loop && step > 0) {
      this.index = Math.round(
        this.track.scrollLeft / step
      );
    } else if (!this.loop) {
      var nearest = 0;
      var distance = Infinity;

      for (var i = 0; i < this.positions.length; i++) {
        var delta = Math.abs(
          this.track.scrollLeft - this.positions[i]
        );

        if (delta < distance) {
          distance = delta;
          nearest = i;
        }
      }

      this.index = nearest;
    }

    this.paint();

    if (this.centerActive) {
      this.markCenter();
    }
  };

  BwSlider.prototype.paint = function () {
    if (!this.dots || !this.dots.length) return;

    var active = this.index;

    if (this.loop) {
      active = (
        (this.index - this.offset) % this.realCount +
        this.realCount
      ) % this.realCount;
    }

    for (var i = 0; i < this.dots.length; i++) {
      var on = i === active;

      this.dots[i].classList.toggle('is-active', on);

      this.dots[i].setAttribute(
        'aria-pressed',
        on ? 'true' : 'false'
      );
    }
  };

  BwSlider.prototype.markCenter = function () {
    var box = this.track.getBoundingClientRect();
    var mid = box.left + box.width / 2;

    var best = null;
    var bestDistance = Infinity;

    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i].getBoundingClientRect();

      var distance = Math.abs(
        (slide.left + slide.width / 2) - mid
      );

      if (distance < bestDistance) {
        bestDistance = distance;
        best = this.slides[i];
      }
    }

    for (var j = 0; j < this.slides.length; j++) {
      this.slides[j].classList.toggle(
        'is-active',
        this.slides[j] === best
      );
    }
  };

  BwSlider.prototype.start = function () {
    var self = this;

    this.stop();

    this.timer = window.setInterval(function () {
      self.go(self.index + 1, true);
    }, this.interval);
  };

  BwSlider.prototype.stop = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  function init() {
    var nodes = document.querySelectorAll('[data-bw-slider]');

    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].dataset.bwSliderReady === 'true') {
        continue;
      }

      nodes[i].dataset.bwSliderReady = 'true';
      new BwSlider(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target || document;

    var nodes = scope.querySelectorAll
      ? scope.querySelectorAll('[data-bw-slider]')
      : [];

    for (var i = 0; i < nodes.length; i++) {
      delete nodes[i].dataset.bwSliderReady;
    }

    init();
  });
})();